import React, { FC, useEffect, useMemo, useState } from 'react';

import { ProductData, StoreRefreshPopup } from '@appcharge/shared-ui';

import useOffersUI from 'api/useOffersUI';
import { OfferPreviewShell } from 'views/Offers/components/OfferPreviewShell';
import { IResolvedProgressBarMission } from 'views/Offers/utils/resolveProgressBarPoints';
import { POPUP_CONTAINER_STYLE } from 'views/OfferDesign/pages/OfferDesignForm/components/OfferDesignPreview/OfferDesignPreview.consts';

const PREVIEW_CONTAINER_ID = 'popup-preview';
const MAX_PREVIEW_PRODUCTS = 4;

interface ITriggeredPopupPreviewProps {
  publisherId?: string;
  currentOfferUI: string;
  currentPrice: number | null;
  previewProducts: ProductData[];
  progressBarPoints?: IResolvedProgressBarMission[];
}

export const TriggeredPopupPreview: FC<ITriggeredPopupPreviewProps> = ({
  publisherId,
  currentOfferUI,
  currentPrice,
  previewProducts,
  progressBarPoints
}): React.ReactElement => {
  const { getOfferUI } = useOffersUI(publisherId, currentOfferUI);
  const offerData = getOfferUI?.data;

  const cappedPreviewProducts = useMemo<ProductData[]>(
    () => previewProducts.slice(0, MAX_PREVIEW_PRODUCTS),
    [previewProducts]
  );

  const safePrice =
    currentPrice === null ||
    currentPrice === undefined ||
    Number.isNaN(currentPrice)
      ? 0
      : currentPrice;

  let buttonText = `$${safePrice}`;
  if (safePrice === 0) {
    buttonText = 'Collect';
  }

  return (
    <OfferPreviewShell
      containerId={PREVIEW_CONTAINER_ID}
      containerStyle={POPUP_CONTAINER_STYLE}
    >
      {() => (
        <PortaledStoreRefreshPopup
          containerId={PREVIEW_CONTAINER_ID}
          title={offerData?.specialOffer?.title || ''}
          titleColor={offerData?.specialOffer?.fontColor?.colorOne}
          titleWeight={offerData?.specialOffer?.fontWeight}
          backgroundColor={offerData?.specialOffer?.backgroundColor?.colorOne}
          backgroundImage={offerData?.backgroundImage}
          borderColor={offerData?.borderColor}
          borderWidth={offerData?.borderWidth}
          buttonText={buttonText}
          products={cappedPreviewProducts}
          progressBarPoints={progressBarPoints}
        />
      )}
    </OfferPreviewShell>
  );
};

interface IPortaledStoreRefreshPopupProps {
  containerId: string;
  title: string;
  titleColor?: string;
  titleWeight?: string;
  backgroundColor?: string;
  backgroundImage?: string;
  borderColor?: React.ComponentProps<typeof StoreRefreshPopup>['borderColor'];
  borderWidth?: number;
  buttonText: string;
  products: ProductData[];
  progressBarPoints?: IResolvedProgressBarMission[];
}

const PortaledStoreRefreshPopup: FC<IPortaledStoreRefreshPopupProps> = ({
  containerId,
  title,
  titleColor,
  titleWeight,
  backgroundColor,
  backgroundImage,
  borderColor,
  borderWidth,
  buttonText,
  products,
  progressBarPoints
}): React.ReactElement | null => {
  const [container, setContainer] = useState<HTMLElement | null>(null);

  useEffect(() => {
    setContainer(document.getElementById(containerId));
  }, [containerId]);

  if (!container) {
    return null;
  }

  return (
    <StoreRefreshPopup
      open
      disableBodyLock
      disableShadow
      onClose={() => {}}
      onCollect={() => {}}
      title={title}
      titleColor={titleColor}
      titleWeight={titleWeight}
      backgroundColor={backgroundColor}
      backgroundImage={backgroundImage}
      borderColor={borderColor}
      borderWidth={borderWidth}
      buttonText={buttonText}
      products={products}
      container={container}
      progressBarPoints={progressBarPoints}
    />
  );
};
